import { memo } from 'react'
import { skills } from '../data/skills'
import type { SkillIconKey } from '../types'

interface SkillIconProps {
  icon: SkillIconKey
}

const SkillIcon = memo(function SkillIcon({ icon }: SkillIconProps) {
  return (
icon === 'react-atom' ? (
  <span className="skill-icon-islands react-atom-icon">
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="reactGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#61dafb', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#00d8ff', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="reactGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g className="electron-orbits">
        <ellipse
          cx="50"
          cy="50"
          rx="35"
          ry="15"
          fill="none"
          stroke="url(#reactGradient)"
          strokeWidth="2"
          className="orbit orbit-1"
          opacity="0.6"
        />
        <ellipse
          cx="50"
          cy="50"
          rx="35"
          ry="15"
          fill="none"
          stroke="url(#reactGradient)"
          strokeWidth="2"
          className="orbit orbit-2"
          opacity="0.6"
          transform="rotate(60 50 50)"
        />
        <ellipse
          cx="50"
          cy="50"
          rx="35"
          ry="15"
          fill="none"
          stroke="url(#reactGradient)"
          strokeWidth="2"
          className="orbit orbit-3"
          opacity="0.6"
          transform="rotate(-60 50 50)"
        />
      </g>

      <g className="electrons">
        <circle
          cx="85"
          cy="50"
          r="3"
          fill="#61dafb"
          className="electron electron-1"
          filter="url(#reactGlow)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="85"
          cy="50"
          r="3"
          fill="#00d8ff"
          className="electron electron-2"
          filter="url(#reactGlow)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur="2.5s"
            repeatCount="indefinite"
            begin="0s"
          />
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="60 50 50"
            to="420 50 50"
            dur="2.5s"
            repeatCount="indefinite"
            additive="sum"
          />
        </circle>
        <circle
          cx="85"
          cy="50"
          r="3"
          fill="#5ed3f3"
          className="electron electron-3"
          filter="url(#reactGlow)"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 50 50"
            to="360 50 50"
            dur="2.8s"
            repeatCount="indefinite"
            begin="0s"
          />
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="-60 50 50"
            to="300 50 50"
            dur="2.8s"
            repeatCount="indefinite"
            additive="sum"
          />
        </circle>
      </g>

      <circle
        cx="50"
        cy="50"
        r="8"
        fill="url(#reactGradient)"
        className="nucleus"
        filter="url(#reactGlow)"
      />
      <circle cx="50" cy="50" r="5" fill="#282c34" className="nucleus-core" />
      <circle cx="50" cy="50" r="3" fill="#61dafb" className="nucleus-inner" />

      <circle
        cx="50"
        cy="50"
        r="10"
        fill="none"
        stroke="#61dafb"
        strokeWidth="0.5"
        className="energy-ring ring-1"
        opacity="0.4"
      />
      <circle
        cx="50"
        cy="50"
        r="10"
        fill="none"
        stroke="#00d8ff"
        strokeWidth="0.5"
        className="energy-ring ring-2"
        opacity="0.3"
      />
    </svg>
  </span>
) : icon === 'server-stack' ? (
  <span className="skill-icon-islands server-stack-icon">
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="serverGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#059669', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient id="dbGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#34d399', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="serverGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g className="server-layers">
        <g className="server-layer layer-3">
          <rect
            x="20"
            y="70"
            width="60"
            height="18"
            rx="3"
            fill="url(#serverGradient)"
            stroke="#047857"
            strokeWidth="1"
          />
          <ellipse cx="50" cy="70" rx="30" ry="5" fill="#34d399" opacity="0.3" />
          <circle
            cx="28"
            cy="79"
            r="2"
            fill="#34d399"
            className="status-light light-3"
          />
          <circle
            cx="35"
            cy="79"
            r="2"
            fill="#34d399"
            className="status-light light-3"
          />
          <rect
            x="45"
            y="76"
            width="25"
            height="2"
            rx="1"
            fill="#047857"
            opacity="0.4"
          />
          <rect
            x="45"
            y="80"
            width="20"
            height="2"
            rx="1"
            fill="#047857"
            opacity="0.4"
          />
        </g>

        <g className="server-layer layer-2">
          <rect
            x="20"
            y="47"
            width="60"
            height="18"
            rx="3"
            fill="url(#serverGradient)"
            stroke="#047857"
            strokeWidth="1"
          />
          <ellipse cx="50" cy="47" rx="30" ry="5" fill="#34d399" opacity="0.3" />
          <circle
            cx="28"
            cy="56"
            r="2"
            fill="#fbbf24"
            className="status-light light-2"
          />
          <circle
            cx="35"
            cy="56"
            r="2"
            fill="#34d399"
            className="status-light light-2"
          />
          <rect
            x="45"
            y="53"
            width="25"
            height="2"
            rx="1"
            fill="#047857"
            opacity="0.4"
          />
          <rect
            x="45"
            y="57"
            width="20"
            height="2"
            rx="1"
            fill="#047857"
            opacity="0.4"
          />
        </g>

        <g className="server-layer layer-1">
          <rect
            x="20"
            y="24"
            width="60"
            height="18"
            rx="3"
            fill="url(#serverGradient)"
            stroke="#047857"
            strokeWidth="1"
          />
          <ellipse cx="50" cy="24" rx="30" ry="5" fill="#34d399" opacity="0.3" />
          <circle
            cx="28"
            cy="33"
            r="2"
            fill="#10b981"
            className="status-light light-1"
          />
          <circle
            cx="35"
            cy="33"
            r="2"
            fill="#10b981"
            className="status-light light-1"
          />
          <rect
            x="45"
            y="30"
            width="25"
            height="2"
            rx="1"
            fill="#047857"
            opacity="0.4"
          />
          <rect
            x="45"
            y="34"
            width="20"
            height="2"
            rx="1"
            fill="#047857"
            opacity="0.4"
          />
        </g>
      </g>

      <g className="database-symbol">
        <ellipse
          cx="50"
          cy="12"
          rx="15"
          ry="5"
          fill="url(#dbGradient)"
          className="db-top"
        />
        <rect
          x="35"
          y="12"
          width="30"
          height="8"
          fill="url(#dbGradient)"
          className="db-body"
        />
        <ellipse
          cx="50"
          cy="20"
          rx="15"
          ry="5"
          fill="#059669"
          className="db-bottom"
        />
        <ellipse
          cx="50"
          cy="12"
          rx="15"
          ry="5"
          fill="none"
          stroke="#34d399"
          strokeWidth="1"
          opacity="0.5"
        />
      </g>

      <g className="data-flow">
        <path
          d="M 50 20 L 50 24"
          stroke="#34d399"
          strokeWidth="2"
          className="flow-line line-1"
          strokeDasharray="2,2"
        />
        <path
          d="M 50 42 L 50 47"
          stroke="#34d399"
          strokeWidth="2"
          className="flow-line line-2"
          strokeDasharray="2,2"
        />
        <path
          d="M 50 65 L 50 70"
          stroke="#34d399"
          strokeWidth="2"
          className="flow-line line-3"
          strokeDasharray="2,2"
        />
      </g>

      <circle
        cx="50"
        cy="22"
        r="2"
        fill="#fbbf24"
        className="data-packet packet-1"
      >
        <animate
          attributeName="cy"
          values="20;24;20"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
      <circle
        cx="50"
        cy="45"
        r="2"
        fill="#60a5fa"
        className="data-packet packet-2"
      >
        <animate
          attributeName="cy"
          values="42;47;42"
          dur="2.5s"
          repeatCount="indefinite"
        />
      </circle>
      <circle
        cx="50"
        cy="67"
        r="2"
        fill="#ec4899"
        className="data-packet packet-3"
      >
        <animate
          attributeName="cy"
          values="65;70;65"
          dur="2.2s"
          repeatCount="indefinite"
        />
      </circle>

      <g className="processing-indicators">
        <rect
          x="72"
          y="30"
          width="3"
          height="8"
          fill="#34d399"
          className="process-bar bar-1"
          opacity="0.6"
        />
        <rect
          x="72"
          y="53"
          width="3"
          height="8"
          fill="#34d399"
          className="process-bar bar-2"
          opacity="0.6"
        />
        <rect
          x="72"
          y="76"
          width="3"
          height="8"
          fill="#34d399"
          className="process-bar bar-3"
          opacity="0.6"
        />
      </g>
    </svg>
  </span>
) : icon === 'cloud-devops' ? (
  <span className="skill-icon-islands cloud-devops-icon">
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient
          id="cloudMainGradient"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient
          id="cloudLightGradient"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" style={{ stopColor: '#93c5fd', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#60a5fa', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="cloudGlowFilter">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Main Cloud Shape */}
      <g className="main-cloud-shape" filter="url(#cloudGlowFilter)">
        {/* Center cloud puff */}
        <ellipse
          cx="50"
          cy="45"
          rx="22"
          ry="16"
          fill="url(#cloudMainGradient)"
          className="cloud-puff puff-1"
        />

        {/* Left cloud puff */}
        <ellipse
          cx="32"
          cy="48"
          rx="14"
          ry="11"
          fill="url(#cloudMainGradient)"
          className="cloud-puff puff-2"
        />

        {/* Right cloud puff */}
        <ellipse
          cx="68"
          cy="48"
          rx="16"
          ry="12"
          fill="url(#cloudMainGradient)"
          className="cloud-puff puff-3"
        />

        {/* Bottom fill */}
        <rect
          x="25"
          y="45"
          width="50"
          height="10"
          fill="url(#cloudMainGradient)"
          className="cloud-base"
        />

        {/* Cloud highlights */}
        <ellipse
          cx="45"
          cy="42"
          rx="10"
          ry="7"
          fill="url(#cloudLightGradient)"
          opacity="0.4"
          className="cloud-highlight"
        />
        <ellipse
          cx="60"
          cy="45"
          rx="8"
          ry="5"
          fill="url(#cloudLightGradient)"
          opacity="0.3"
          className="cloud-highlight"
        />
      </g>

      {/* Docker Container Icons */}
      <g className="docker-containers">
        <rect
          x="35"
          y="60"
          width="10"
          height="12"
          rx="1"
          fill="#2496ed"
          className="container container-1"
          opacity="0.8"
        />
        <rect
          x="35"
          y="60"
          width="10"
          height="3"
          fill="#1d7fc7"
          className="container-top"
        />
        <circle
          cx="37"
          cy="66"
          r="0.8"
          fill="#ffffff"
          className="container-dot"
        />
        <circle
          cx="43"
          cy="66"
          r="0.8"
          fill="#ffffff"
          className="container-dot"
        />

        <rect
          x="48"
          y="62"
          width="10"
          height="12"
          rx="1"
          fill="#2496ed"
          className="container container-2"
          opacity="0.8"
        />
        <rect
          x="48"
          y="62"
          width="10"
          height="3"
          fill="#1d7fc7"
          className="container-top"
        />
        <circle
          cx="50"
          cy="68"
          r="0.8"
          fill="#ffffff"
          className="container-dot"
        />
        <circle
          cx="56"
          cy="68"
          r="0.8"
          fill="#ffffff"
          className="container-dot"
        />

        <rect
          x="61"
          y="64"
          width="10"
          height="12"
          rx="1"
          fill="#2496ed"
          className="container container-3"
          opacity="0.8"
        />
        <rect
          x="61"
          y="64"
          width="10"
          height="3"
          fill="#1d7fc7"
          className="container-top"
        />
        <circle
          cx="63"
          cy="70"
          r="0.8"
          fill="#ffffff"
          className="container-dot"
        />
        <circle
          cx="69"
          cy="70"
          r="0.8"
          fill="#ffffff"
          className="container-dot"
        />
      </g>

      {/* Data Upload/Download Arrows */}
      <g className="cloud-data-flow">
        <path
          d="M 40 55 L 40 60"
          stroke="#fbbf24"
          strokeWidth="2"
          className="upload-arrow arrow-1"
          strokeLinecap="round"
        />
        <path
          d="M 37 57 L 40 60 L 43 57"
          stroke="#fbbf24"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        <path
          d="M 55 55 L 55 60"
          stroke="#10b981"
          strokeWidth="2"
          className="download-arrow arrow-2"
          strokeLinecap="round"
        />
        <path
          d="M 52 57 L 55 54 L 58 57"
          stroke="#10b981"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>

      {/* Kubernetes Wheel */}
      <g className="k8s-wheel">
        <circle
          cx="25"
          cy="30"
          r="6"
          fill="none"
          stroke="#326ce5"
          strokeWidth="1.5"
          className="k8s-circle"
        />
        <circle cx="25" cy="30" r="2" fill="#326ce5" className="k8s-center" />
        <line
          x1="25"
          y1="24"
          x2="25"
          y2="27"
          stroke="#326ce5"
          strokeWidth="1"
          className="k8s-spoke"
        />
        <line
          x1="25"
          y1="33"
          x2="25"
          y2="36"
          stroke="#326ce5"
          strokeWidth="1"
          className="k8s-spoke"
        />
        <line
          x1="19"
          y1="30"
          x2="22"
          y2="30"
          stroke="#326ce5"
          strokeWidth="1"
          className="k8s-spoke"
        />
        <line
          x1="28"
          y1="30"
          x2="31"
          y2="30"
          stroke="#326ce5"
          strokeWidth="1"
          className="k8s-spoke"
        />
      </g>

      {/* CI/CD Pipeline */}
      <g className="cicd-pipeline">
        <rect
          x="70"
          y="28"
          width="8"
          height="8"
          rx="1"
          fill="#10b981"
          className="pipeline-stage stage-1"
        />
        <rect
          x="78"
          y="28"
          width="2"
          height="8"
          fill="#34d399"
          opacity="0.6"
          className="pipeline-progress progress-1"
        />

        <path
          d="M 80 32 L 84 32"
          stroke="#6366f1"
          strokeWidth="1.5"
          className="pipeline-connector"
        />

        <rect
          x="84"
          y="28"
          width="8"
          height="8"
          rx="1"
          fill="#6366f1"
          className="pipeline-stage stage-2"
        />
        <rect
          x="92"
          y="28"
          width="2"
          height="8"
          fill="#818cf8"
          opacity="0.6"
          className="pipeline-progress progress-2"
        />
      </g>

      {/* Floating Data Particles */}
      <circle
        cx="30"
        cy="38"
        r="1"
        fill="#fbbf24"
        className="data-particle particle-1"
      />
      <circle
        cx="70"
        cy="42"
        r="1.2"
        fill="#60a5fa"
        className="data-particle particle-2"
      />
      <circle
        cx="50"
        cy="35"
        r="0.8"
        fill="#10b981"
        className="data-particle particle-3"
      />
      <circle
        cx="65"
        cy="40"
        r="1"
        fill="#ec4899"
        className="data-particle particle-4"
      />

      {/* AWS Logo Reference (simple) */}
      <g className="aws-indicator">
        <path d="M 12 65 L 15 68 L 12 71" fill="#ff9900" className="aws-smile" />
        <circle cx="11" cy="66" r="0.8" fill="#ff9900" />
        <circle cx="11" cy="70" r="0.8" fill="#ff9900" />
      </g>
    </svg>
  </span>
) : icon === 'ai-brain' ? (
  <span className="skill-icon-islands ai-brain-icon">
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="brainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#a78bfa', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#8b5cf6', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient id="neuronGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#c084fc', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#a78bfa', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="brainGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="neuronGlow">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Brain Outline */}
      <g className="brain-structure" filter="url(#brainGlow)">
        {/* Left hemisphere */}
        <path
          d="M 30 30 Q 25 35 25 45 Q 25 55 30 65 Q 35 70 45 70 L 45 30 Q 35 25 30 30 Z"
          fill="url(#brainGradient)"
          className="brain-hemisphere left-brain"
          opacity="0.7"
        />

        {/* Right hemisphere */}
        <path
          d="M 70 30 Q 75 35 75 45 Q 75 55 70 65 Q 65 70 55 70 L 55 30 Q 65 25 70 30 Z"
          fill="url(#brainGradient)"
          className="brain-hemisphere right-brain"
          opacity="0.7"
        />

        {/* Brain folds/wrinkles */}
        <path
          d="M 30 35 Q 35 37 40 35"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="brain-fold"
          opacity="0.6"
        />
        <path
          d="M 28 45 Q 33 47 38 45"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="brain-fold"
          opacity="0.6"
        />
        <path
          d="M 30 55 Q 35 57 40 55"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="brain-fold"
          opacity="0.6"
        />

        <path
          d="M 70 35 Q 65 37 60 35"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="brain-fold"
          opacity="0.6"
        />
        <path
          d="M 72 45 Q 67 47 62 45"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="brain-fold"
          opacity="0.6"
        />
        <path
          d="M 70 55 Q 65 57 60 55"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="brain-fold"
          opacity="0.6"
        />
      </g>

      {/* Neural Network Nodes */}
      <g className="neural-nodes">
        {/* Layer 1 - Input */}
        <circle
          cx="35"
          cy="40"
          r="3"
          fill="url(#neuronGradient)"
          className="neuron input-node node-1"
          filter="url(#neuronGlow)"
        />
        <circle
          cx="35"
          cy="50"
          r="3"
          fill="url(#neuronGradient)"
          className="neuron input-node node-2"
          filter="url(#neuronGlow)"
        />
        <circle
          cx="35"
          cy="60"
          r="3"
          fill="url(#neuronGradient)"
          className="neuron input-node node-3"
          filter="url(#neuronGlow)"
        />

        {/* Layer 2 - Hidden */}
        <circle
          cx="50"
          cy="38"
          r="3.5"
          fill="url(#neuronGradient)"
          className="neuron hidden-node node-4"
          filter="url(#neuronGlow)"
        />
        <circle
          cx="50"
          cy="50"
          r="4"
          fill="url(#neuronGradient)"
          className="neuron hidden-node node-5"
          filter="url(#neuronGlow)"
        />
        <circle
          cx="50"
          cy="62"
          r="3.5"
          fill="url(#neuronGradient)"
          className="neuron hidden-node node-6"
          filter="url(#neuronGlow)"
        />

        {/* Layer 3 - Output */}
        <circle
          cx="65"
          cy="45"
          r="3"
          fill="url(#neuronGradient)"
          className="neuron output-node node-7"
          filter="url(#neuronGlow)"
        />
        <circle
          cx="65"
          cy="55"
          r="3"
          fill="url(#neuronGradient)"
          className="neuron output-node node-8"
          filter="url(#neuronGlow)"
        />
      </g>

      {/* Neural Connections */}
      <g className="neural-connections">
        {/* Input to Hidden Layer */}
        <line
          x1="35"
          y1="40"
          x2="50"
          y2="38"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-1"
          opacity="0.4"
        />
        <line
          x1="35"
          y1="40"
          x2="50"
          y2="50"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-2"
          opacity="0.4"
        />
        <line
          x1="35"
          y1="50"
          x2="50"
          y2="38"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-3"
          opacity="0.4"
        />
        <line
          x1="35"
          y1="50"
          x2="50"
          y2="50"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-4"
          opacity="0.4"
        />
        <line
          x1="35"
          y1="50"
          x2="50"
          y2="62"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-5"
          opacity="0.4"
        />
        <line
          x1="35"
          y1="60"
          x2="50"
          y2="50"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-6"
          opacity="0.4"
        />
        <line
          x1="35"
          y1="60"
          x2="50"
          y2="62"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-7"
          opacity="0.4"
        />

        {/* Hidden to Output Layer */}
        <line
          x1="50"
          y1="38"
          x2="65"
          y2="45"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-8"
          opacity="0.4"
        />
        <line
          x1="50"
          y1="38"
          x2="65"
          y2="55"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-9"
          opacity="0.4"
        />
        <line
          x1="50"
          y1="50"
          x2="65"
          y2="45"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-10"
          opacity="0.4"
        />
        <line
          x1="50"
          y1="50"
          x2="65"
          y2="55"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-11"
          opacity="0.4"
        />
        <line
          x1="50"
          y1="62"
          x2="65"
          y2="45"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-12"
          opacity="0.4"
        />
        <line
          x1="50"
          y1="62"
          x2="65"
          y2="55"
          stroke="#c084fc"
          strokeWidth="1"
          className="connection conn-13"
          opacity="0.4"
        />
      </g>

      {/* Data Signals */}
      <g className="data-signals">
        <circle
          cx="35"
          cy="40"
          r="1.5"
          fill="#fbbf24"
          className="signal signal-1"
        >
          <animate
            attributeName="cx"
            values="35;50;65"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="cy"
            values="40;50;45"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="35"
          cy="50"
          r="1.5"
          fill="#60a5fa"
          className="signal signal-2"
        >
          <animate
            attributeName="cx"
            values="35;50;65"
            dur="2.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="cy"
            values="50;50;55"
            dur="2.5s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx="35"
          cy="60"
          r="1.5"
          fill="#ec4899"
          className="signal signal-3"
        >
          <animate
            attributeName="cx"
            values="35;50;65"
            dur="2.2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="cy"
            values="60;62;45"
            dur="2.2s"
            repeatCount="indefinite"
          />
        </circle>
      </g>

      {/* AI Sparkles */}
      <g className="ai-sparkles">
        <circle
          cx="25"
          cy="30"
          r="1"
          fill="#fbbf24"
          className="sparkle sparkle-1"
        />
        <circle
          cx="75"
          cy="32"
          r="1.2"
          fill="#c084fc"
          className="sparkle sparkle-2"
        />
        <circle
          cx="20"
          cy="50"
          r="0.8"
          fill="#60a5fa"
          className="sparkle sparkle-3"
        />
        <circle
          cx="80"
          cy="48"
          r="1"
          fill="#ec4899"
          className="sparkle sparkle-4"
        />
        <circle
          cx="28"
          cy="68"
          r="1.1"
          fill="#fbbf24"
          className="sparkle sparkle-5"
        />
        <circle
          cx="72"
          cy="70"
          r="0.9"
          fill="#a78bfa"
          className="sparkle sparkle-6"
        />
      </g>

      {/* Thinking Waves */}
      <g className="thinking-waves">
        <path
          d="M 45 20 Q 50 18 55 20"
          fill="none"
          stroke="#a78bfa"
          strokeWidth="1.5"
          className="wave wave-1"
          opacity="0.5"
        />
        <path
          d="M 42 25 Q 50 22 58 25"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="1.5"
          className="wave wave-2"
          opacity="0.4"
        />
        <path
          d="M 40 30 Q 50 26 60 30"
          fill="none"
          stroke="#7c3aed"
          strokeWidth="1.5"
          className="wave wave-3"
          opacity="0.3"
        />
      </g>

      {/* Processing Indicator */}
      <g className="processing-indicator">
        <circle
          cx="50"
          cy="85"
          r="3"
          fill="none"
          stroke="#a78bfa"
          strokeWidth="1"
          className="process-ring ring-1"
        />
        <circle
          cx="50"
          cy="85"
          r="3"
          fill="none"
          stroke="#8b5cf6"
          strokeWidth="1"
          className="process-ring ring-2"
        />
        <circle cx="50" cy="85" r="1.5" fill="#c084fc" className="process-core" />
      </g>
    </svg>
  </span>
) : icon === 'security-shield' ? (
  <span className="skill-icon-islands security-shield-icon">
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#34d399', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#059669', stopOpacity: 1 }} />
        </linearGradient>
        <linearGradient id="shieldInnerGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style={{ stopColor: '#6ee7b7', stopOpacity: 1 }} />
          <stop offset="100%" style={{ stopColor: '#10b981', stopOpacity: 1 }} />
        </linearGradient>
        <filter id="shieldGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Shield outline */}
      <g className="shield-structure" filter="url(#shieldGlow)">
        <path
          d="M 50 12 L 78 24 L 78 48 Q 78 72 50 88 Q 22 72 22 48 L 22 24 Z"
          fill="url(#shieldGradient)"
          className="shield-body"
          opacity="0.85"
        />
        <path
          d="M 50 20 L 70 29 L 70 48 Q 70 66 50 79 Q 30 66 30 48 L 30 29 Z"
          fill="url(#shieldInnerGradient)"
          className="shield-inner"
          opacity="0.5"
        />
      </g>

      {/* Scanning sweep ring */}
      <g className="shield-scan">
        <circle
          cx="50"
          cy="48"
          r="26"
          fill="none"
          stroke="#a7f3d0"
          strokeWidth="1"
          strokeDasharray="6 10"
          className="scan-ring"
          opacity="0.6"
        />
      </g>

      {/* Lock body */}
      <g className="lock-group">
        <path
          d="M 43 42 L 43 36 Q 43 29 50 29 Q 57 29 57 36 L 57 42"
          fill="none"
          stroke="#ecfdf5"
          strokeWidth="3"
          strokeLinecap="round"
          className="lock-shackle"
        />
        <rect
          x="39"
          y="42"
          width="22"
          height="18"
          rx="3"
          fill="#ecfdf5"
          className="lock-body"
        />
        <circle cx="50" cy="49" r="2.4" fill="#059669" className="lock-keyhole-dot" />
        <rect
          x="49"
          y="49"
          width="2"
          height="6"
          fill="#059669"
          className="lock-keyhole-stem"
        />
      </g>

      {/* Orbiting security pulses */}
      <g className="security-pulses">
        <circle cx="50" cy="16" r="2" fill="#a7f3d0" className="pulse-dot pulse-1" />
        <circle cx="76" cy="48" r="1.6" fill="#6ee7b7" className="pulse-dot pulse-2" />
        <circle cx="50" cy="80" r="1.6" fill="#a7f3d0" className="pulse-dot pulse-3" />
        <circle cx="24" cy="48" r="1.6" fill="#6ee7b7" className="pulse-dot pulse-4" />
      </g>

      {/* Checkmark reveal */}
      <path
        d="M 43 49 L 48 54 L 58 43"
        fill="none"
        stroke="#a7f3d0"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shield-checkmark"
        opacity="0.85"
      />
    </svg>
  </span>
) : (
  <span className="skill-icon-islands">{icon}</span>
)
  )
})

export const Skills = memo(function Skills() {
  return (
<section id="skills" className="section-islands skills-islands">
  <div className="container-islands">
    <div className="section-header-islands">
      <span className="section-label-islands">Technical Expertise</span>
      <h2 className="section-title-islands">Full-Stack Mastery</h2>
    </div>

    <div className="skills-grid-islands">
      {skills.map((skillGroup, index) => (
        <div
          key={index}
          className="island skill-island"
          style={{
            transform: `translateY(calc(var(--scroll-y, 0) * ${-0.04 * (index + 1)} * 1px)) translateX(calc(var(--mouse-x, 0) * 0.5px))`,
            animationDelay: `${index * 0.1}s`,
          }}
        >
          <div className="skill-header-islands">
                  <SkillIcon icon={skillGroup.icon} />
            <h3 className="skill-category-islands">{skillGroup.category}</h3>
          </div>
          <div className="skill-items-islands">
            {skillGroup.items.map((item, i) => (
              <span key={i} className="skill-chip-islands">
                {item}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
</section>
  )
})
